import React, { useContext, useEffect, useState } from 'react';
import './SubscriptionSummary.css';
import MiniAppMessageTypes from '../../services/Constants/MiniAppMessageTypes.js';
import DualButtons from '../../components/DualButtons/DualButtons.jsx';
import TopBack from '../../components/TopBack/TopBack.jsx';
import KeyValueList from '../../components/KeyValueList/KeyValueList.jsx';
import PhoneCall from '../../assets/PhoneCall.jsx';
import BillIcon from '../../assets/BillIcon.jsx';
import { SubscriptionDetailsContext, UpdateSubscriptionDetailsContext } from '../../services/context/SubscriptionDetailsContext.js';
import moment from 'moment';
import { BearerTokenContext } from '../../services/context/BearerTokenContext.js';
import useApis from '../../services/hooks/useApis.js';
import { NetworkError } from '../../services/Constants/Errors.js';
import { captureException } from '@sentry/react';
import { useNavigate, useLocation } from 'react-router-dom';
import ROUTES from '../../services/Constants/GlobalRoutes.jsx';
import { UserDetailsContext } from '../../services/context/UserDetailsContext.js';
import { UpdateSnackBarContext } from '../../services/context/SnackBarContext.js';
import FailIcon from '../../assets/FailIcon.jsx';
import { UpdateRocketContext } from '../../services/context/RocketContext.js';
import Loading from '../../components/Loading/Loading.jsx';
import { defaultAnalyticsVariables, events, pagePrefix } from '../../services/Constants/Analytics.js';
import { ConfigContext } from '../../services/context/ConfigContext.js';
// import { MoneyFormatterContext } from '../../services/context/MoneyFormatterContext.js';
import useNormalizedSubscriptionStatus from '../../services/utils/subscriptionUtils.js';
import { UpdateMiniAppSubscriptionContext } from '../../services/context/MiniAppContext.js';

/**
 * @returns {JSX.Element} CartPage component
 */
export default function SubscriptionSummary() {
  const userDetails = useContext(UserDetailsContext);
  const bearerToken = useContext(BearerTokenContext);
  let subscriptionDetails = useContext(SubscriptionDetailsContext);
  const setSubscriptionDetails = useContext(UpdateSubscriptionDetailsContext);
  const setSnackBar = useContext(UpdateSnackBarContext);
  const setRocket = useContext(UpdateRocketContext);
  const [loading, setLoading] = useState(/** @type {string} */null);
  const { getSubscriptionDetails, activateSubscription } = useApis();
  const navigateTo = useNavigate();
  const config = useContext(ConfigContext);
  const [, setPaymentResponse] = useState(null);
  const setSubscriptionHandler = useContext(UpdateMiniAppSubscriptionContext);
  const location = useLocation();
  // Retrieve selected plan
  const { selectedPlan } = location.state || {};

  const getPlanDetails = plan => {
    if (!plan || !plan.frequency) {
      return {
        planValidity: '',
        paymentDateForSubscription: '',
      };
    }

    let planValidity = '';
    let unit = '';

    switch (plan.frequency) {
      case 'D': {
        planValidity = 'Daily';
        unit = 'days';
        break;
      }

      case 'W': {
        planValidity = 'Weekly';
        unit = 'weeks';
        break;
      }

      case 'M': {
        planValidity = 'Monthly';
        unit = 'months';
        break;
      }

      case 'Y': {
        planValidity = 'Yearly';
        unit = 'years';
        break;
      }

      default: {
        return {
          planValidity: '',
          paymentDateForSubscription: '',
        };
      }
    }

    const paymentDateForSubscription = moment()
      .add(1, unit)
      .format('D MMMM YYYY');

    return {
      planValidity,
      paymentDateForSubscription,
    };
  };

  const { planValidity, paymentDateForSubscription } = getPlanDetails(selectedPlan);
  let subscriptionFee = `R${selectedPlan?.price}`;
  let packageId = selectedPlan?.packageId;

  const airtimeSubscriberTypes = [
    'PREPAID',
    'HYBRID',
  ];
  const totalAirtimeRemaining = localStorage.getItem('Airtime-Remaining');
  const subscriberType = localStorage.getItem('subscriberType');
  const textOnSuccessPage = subscriberType === 'PREPAID' ? 'your airtime' : 'your bill';
  const subscriptionStatus = useNormalizedSubscriptionStatus();

  useEffect(() => {
    window.utag?.view({
      ...defaultAnalyticsVariables,
      event_name: [events.pageView],
      page_name: `${pagePrefix}: subscription: summary`,
      visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
    });
  }, []);

  const onClickAnalytics = () => {
    window.utag?.view({
      ...defaultAnalyticsVariables,
      event_name: [events.interaction],
      page_name: `${pagePrefix}: subscription: summary`,
      link_id: `${pagePrefix}: subscription summary: subscribe`,
      visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
    });
  };

  const onCancelClickAnalytics = () => {
    window.utag?.view({
      ...defaultAnalyticsVariables,
      event_name: [events.interaction],
      page_name: `${pagePrefix}: subscription: summary`,
      link_id: `${pagePrefix}: subscription summary: cancel`,
      visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
    });
  };

  const checkExpiryTime = () => {
    const currentTime = Date.now();
    const timeDifference = currentTime - subscriptionDetails?.expiryDate;
    return timeDifference > 3 * 60 * 1000;
  };

  // Subscribe user API call
  const subscribeUserApiCall = (planPackageId, planSubscriptionFee) => {
    const info = {
      icon: <FailIcon />,
      body: '',
    };
    setLoading('Loading...');
    activateSubscription(bearerToken, planPackageId)
      .then(response => {
        switch (response.status) {
          case 200: {
            const subscriptionAmount = planSubscriptionFee;
            setSubscriptionHandler(() => message => setPaymentResponse(message.data));
            window.my?.postMessage({
              messageType: MiniAppMessageTypes.subscription,
              data: {
                adjustData: {
                  token: config.frontend.adjustTokens.paymentOptions.vodaPayClub,
                  revenue: {
                    amount: subscriptionAmount,
                    currency: 'ZAR',
                  },
                },
              },
            }, '*');

            setRocket({
              analytics: {
                ...defaultAnalyticsVariables,
                event_name: [
                  events.pageView,
                  events.subscribeSuccess,
                  events.transactionSuccess,
                ],
                page_name: `${pagePrefix}: subscription activated`,
                visitor_id_user: userDetails.userId,
                visitor_id_asset_active: userDetails.msisdn,
                visitor_id_marketing_email: userDetails.email,
                visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
              },
              body: <p className='sub-text'>You&apos;re now a member of the exclusive VodaPay Club!
                We&apos;ll deduct your payment from {textOnSuccessPage} on {paymentDateForSubscription}.</p>,
              header: <p className='text'>Purchase Successful!</p>,
              onContinue: () => navigateTo(ROUTES.HOME, { replace: true }),
              journeyType: 'subscription',
            });
            navigateTo(ROUTES.SUCCESS);
            sessionStorage.removeItem('selectedPlan');
            setSubscriptionDetails(null);
            break;
          }

          case 409: {
            setLoading(null);
            info.body = 'Subscription failed due to insufficient funds.';
            setSnackBar({ info });
            break;
          }

          case 423: {
            setLoading(null);
            info.body = 'Customer profile is restricted.';
            setSnackBar({ info });
            break;
          }

          default: {
            setLoading(null);
            info.body = 'Something went wrong. Please try again later.';
            setSnackBar({ info });
          }
        }
      })
      .catch(error => {
        setLoading(null);
        if (error) {
          navigateTo(ROUTES.ROCKET_ERROR);
        }
      });
  };

  // On click of subscribe option
  const onClickSubscribe = () => {
    const timeExceeded = checkExpiryTime();
    if (timeExceeded) {
      setLoading('Loading subscription details...');
      getSubscriptionDetails(bearerToken)
        .then(response => {
          subscriptionDetails = null;
          subscriptionFee = null;
          const expiryDate = Date.now();

          const plans = response?.data?.plans || [];
          // Find the selected plan in the plans array based on frequency
          const selectedPlanFromResponse = plans.find(plan => plan.frequency === selectedPlan?.frequency);
          // Get the packageId if found
          packageId = selectedPlanFromResponse?.packageId || null;
          subscriptionFee = `R${selectedPlanFromResponse?.price}`;

          setLoading(null);
          subscriptionDetails = {
            packageId,
            subscriptionFee,
            expiryDate,
          };
          subscribeUserApiCall(packageId, subscriptionFee);
        })
        .catch(error => {
          setLoading(null);
          subscriptionDetails = null;
          if (error instanceof NetworkError) captureException(new Error(`Failed to get summray details: ${error.response.status}`));
          else captureException(error);
          navigateTo(ROUTES.ROCKET_ERROR);
        });
    } else {
      subscribeUserApiCall(packageId, subscriptionFee);
    }
  };

  const maskMsisdn = number => {
    const value = number.toString().slice(-4);
    return value.padStart(number.toString().length, '*');
  };

  return (
    <section className='subscription-summary v-container'>
      <TopBack />
      <div className='summary-container'>
        <div className='summary-heading'>Summary</div>
        <div className='details-container'>
          <section className='voucher-details'>
            <KeyValueList object={
              {
                'Product name': 'VodaLend VodaPay Club',
                'Next payment date': paymentDateForSubscription,
                'Payment frequency': planValidity,
                'Subscription fee': subscriptionFee,
              }
            }
            />
          </section>
        </div>
      </div>
      <h3 className='select-payment-method'>Payment method:</h3>
      {
        airtimeSubscriberTypes.includes(subscriberType) ? (
          <div className='airtime'>
            <div className='icon-details-container'>
              <div className='airtime-icon'>
                <PhoneCall />
              </div>
              <div className='airtime-details'>
                <div className='airtime-text'>Airtime</div>
                <div className='airtime-balance'>Available: {totalAirtimeRemaining}</div>
              </div>
            </div>
          </div>
        ) : (<div className='airtime'>
          <div className='icon-details-container'>
            <div className='airtime-icon'>
              <BillIcon />
            </div>
            <div className='airtime-details'>
              <div className='airtime-text'>Add to bill</div>
              <div className='airtime-balance'>Account {maskMsisdn(userDetails.msisdn)}</div>
            </div>
          </div>
        </div>
        )
      }
      <h3 className='cost-details-heading'>Cost details:</h3>
      <div className='cost-details-container'>
        <div className='cost-details-text'>Total</div>
        <div className='cost-details-balance'>{selectedPlan?.displayName}</div>
      </div>
      <p className='footer-condition'>By tapping &quot;Subscribe&quot; you agree to the Vouchers
        <button
          className='footer-condition-link'
          onClick={ () => window.my.postMessage({ messageType: MiniAppMessageTypes.termsAndCond }, '*') }
        ><a>Terms & Conditions</a></button>
      </p>
      <div className='dual-buttons-container'>
        <DualButtons
          primaryOnClick={
            () => {
              onClickSubscribe();
              onClickAnalytics();
            }
          }
          secondaryOnClick={
            () => {
              navigateTo(-1);
              onCancelClickAnalytics();
            }
          }
          primaryText={ 'Subscribe' }
          secondaryText={ 'Cancel' }
        />
      </div>
      { loading && <Loading headerText={ loading } /> }
    </section>
  );
}
